import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, Globe, Trophy, Sparkle } from "@phosphor-icons/react";

const TIER = {
    direct: { label: "COMPETIDOR DIRECTO · ≥3 compartidos", cls: "bg-[var(--ink)] text-white border-[var(--ink)]" },
    partial: { label: "Parcial · 2 compartidos", cls: "bg-[var(--cream)] border-[var(--line)]" },
    weak: { label: "Débil · 1 compartido", cls: "text-[var(--ink-3)]" },
};

export default function CompetitorsTab({ formula, onUpdated }) {
    const [busy, setBusy] = useState(!!formula.competitors_analyzing);
    const pollRef = useRef(null);

    const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    const startPoll = () => {
        stopPoll();
        let tries = 0;
        pollRef.current = setInterval(async () => {
            tries += 1;
            try {
                const { data } = await api.get(`/formulas/${formula.id}`);
                if (!data.competitors_analyzing) {
                    onUpdated(data);
                    setBusy(false);
                    stopPoll();
                    toast.success("Ranking de competidores actualizado");
                }
            } catch {}
            if (tries > 36) { stopPoll(); setBusy(false); }
        }, 5000);
    };

    useEffect(() => {
        if (formula.competitors_analyzing) { setBusy(true); startPoll(); }
        return stopPoll;
        // eslint-disable-next-line
    }, [formula.id]);

    const recompute = async () => {
        try {
            await api.post(`/formulas/${formula.id}/competitors`);
            setBusy(true);
            startPoll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo lanzar el análisis");
        }
    };

    const ranked = formula.competitors_ranked || [];

    return (
        <div className="max-w-4xl space-y-6" data-testid="competitors-tab">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <p className="text-sm text-[var(--ink-2)] max-w-xl">
                    Productos del mercado que comparten composición real con esta fórmula. Solo se consideran
                    <strong> competidores directos</strong> los que comparten <strong>≥3 principios activos</strong> (aunque varíe la concentración).
                </p>
                <button onClick={recompute} disabled={busy}
                    className="px-3 py-2 border hairline bg-white text-xs hover:bg-[var(--cream)] inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="competitors-recompute-button">
                    {busy ? <Sparkle size={13} className="animate-pulse"/> : <ArrowsClockwise size={13}/>}
                    {busy ? "Buscando y rankeando…" : "Recalcular ranking"}
                </button>
            </div>

            {formula.competitor_market_summary && (
                <div className="border hairline bg-white p-5" data-testid="competitors-market-summary">
                    <div className="label-eyebrow mb-2 flex items-center gap-2"><Globe size={13}/> Lectura de mercado</div>
                    <p className="text-sm leading-relaxed">{formula.competitor_market_summary}</p>
                </div>
            )}

            {ranked.length > 0 ? (
                <div className="space-y-3">
                    {ranked.map((c, i) => {
                        const tier = TIER[c.tier] || TIER.weak;
                        return (
                            <div key={i} className="border hairline bg-white p-5" data-testid={`competitor-ranked-${i}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="font-display text-2xl text-[var(--ink-3)] w-8 flex-shrink-0">{i + 1}</div>
                                        <div className="min-w-0">
                                            <a href={c.url} target="_blank" rel="noreferrer" className="font-medium text-[var(--sage)] hover:underline">
                                                {c.title}{c.brand ? ` — ${c.brand}` : ""}
                                            </a>
                                            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                                                <span className={`citation-badge ${tier.cls}`}>{c.tier === "direct" && <Trophy size={11} className="inline mr-1"/>}{tier.label}</span>
                                                {c.est_price_eur != null && <span className="mono text-xs text-[var(--ink-2)]">~{c.est_price_eur} €</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <div className="mono text-xs text-[var(--ink-2)] mb-1">proximidad {c.proximity_score ?? "—"}/100</div>
                                        <div className="w-28 h-1.5 bg-[var(--cream)] border hairline">
                                            <div className="h-full bg-[var(--sage)]" style={{ width: `${Math.min(c.proximity_score || 0, 100)}%` }}/>
                                        </div>
                                    </div>
                                </div>
                                {(c.shared_ingredients || []).length > 0 && (
                                    <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                                        <span className="text-[10px] uppercase tracking-wider text-[var(--ink-3)]">Comparte:</span>
                                        {c.shared_ingredients.map((s, j) => (
                                            <span key={j} className="text-xs bg-[var(--cream)] border hairline px-2 py-0.5">{s}</span>
                                        ))}
                                    </div>
                                )}
                                {c.composition_estimate && <p className="text-xs text-[var(--ink-2)] mt-2 mono">{c.composition_estimate}</p>}
                                {c.notes && <p className="text-sm mt-2 leading-relaxed">{c.notes}</p>}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="border hairline bg-white p-6 text-sm text-[var(--ink-3)]" data-testid="competitors-empty">
                    {busy ? "Buscando productos en el mercado y rankeando por composición compartida…"
                        : "Aún no hay ranking de competidores. Lanza el análisis para buscar productos con ≥3 ingredientes compartidos."}
                </div>
            )}

            {(formula.competitors || []).length > 0 && (
                <details className="border hairline bg-white">
                    <summary className="px-5 py-3 text-xs text-[var(--ink-2)] cursor-pointer hover:bg-[var(--cream)]" data-testid="competitors-raw-toggle">
                        Resultados de búsqueda sin filtrar ({formula.competitors.length})
                    </summary>
                    <ul className="px-5 pb-4 space-y-2">
                        {formula.competitors.map((c, i) => (
                            <li key={i} className="border-b hairline last:border-b-0 py-2">
                                <a href={c.url} target="_blank" rel="noreferrer" className="text-sm text-[var(--sage)] hover:underline">{c.title}</a>
                                <p className="text-xs text-[var(--ink-3)] line-clamp-1">{c.snippet}</p>
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}
